import { useEffect, useRef, useState } from 'react';

/**
 * useLipSync —— 用音频音量包络驱动虚拟人口型
 * ============================================================
 *
 * 原理：
 *   把页面上已有的 <audio> 元素接入 Web Audio 图（createMediaElementSource），
 *   后面挂一个 AnalyserNode，每个动画帧读取时域波形并算出 RMS 音量，
 *   再把音量离散成 0/1/2 三档口型（闭嘴 / 半开 / 全开）。
 *
 * 为什么不用音素级口型（viseme）：
 *   音素级需要强制对齐或流式音素识别，成本和延迟都高一个数量级。
 *   对「虚拟人在说话」这一观感目标，音量包络驱动的效果已经足够，
 *   且完全运行在客户端、零额外请求。后续若要做精细口型，只需替换本 hook
 *   的输出（把 mouth 从 0/1/2 换成 viseme 序列），组件层无需改动。
 *
 * ⚠️ 两个必须注意的坑：
 *   1. createMediaElementSource() 对同一个元素**只能调用一次**，重复调用会抛
 *      InvalidStateError。这里用模块级 WeakMap 缓存已绑定的图，跨组件重挂载复用。
 *   2. source 必须 connect 到 destination，否则 <audio> 会被静音
 *      （因为音频被路由进了 Web Audio 图，不再走默认输出）。
 *
 * 可测性：
 *   核心的三步计算（RMS → 包络平滑 → 口型档位）被拆成下面三个**纯函数**并导出，
 *   因此可以在 Node 里直接做单元测试，不需要浏览器或真实音频。
 *   见 frontend/mobile/_smoke/virtualtutor.smoke.jsx。
 */

/** 元素 → { ctx, source, analyser, data } 的模块级缓存 */
const audioGraphCache = new WeakMap();

/** 口型阈值（RMS，归一化到 0..1） */
export const MOUTH_HALF_THRESHOLD = 0.025;
export const MOUTH_OPEN_THRESHOLD = 0.08;

/** 包络衰减系数：越大越跟手，越小越平滑（避免口型抖动） */
export const ENVELOPE_DECAY = 0.78;

/** 电平平滑后的显示增益（音量通常偏小，适当放大便于驱动动画） */
export const LEVEL_GAIN = 4;

/**
 * 由 AnalyserNode 的时域数据计算 RMS 音量
 *
 * 说明：getByteTimeDomainData 返回 0..255 的无符号字节，128 代表零点。
 *
 * @param {Uint8Array|number[]} timeDomainData - 时域波形（0..255）
 * @returns {number} RMS 音量，约 0..1
 */
export function computeRms(timeDomainData) {
  if (!timeDomainData || timeDomainData.length === 0) {
    return 0;
  }
  let sumSquares = 0;
  for (let i = 0; i < timeDomainData.length; i += 1) {
    const v = (timeDomainData[i] - 128) / 128;
    sumSquares += v * v;
  }
  return Math.sqrt(sumSquares / timeDomainData.length);
}

/**
 * 包络平滑：攻击快（立刻跟上），释放慢（避免口型闪烁）
 *
 * @param {number} rms          - 当前帧的 RMS
 * @param {number} previous     - 上一帧的包络值
 * @param {number} [decay]      - 衰减系数
 * @returns {number} 平滑后的包络
 */
export function smoothEnvelope(rms, previous, decay = ENVELOPE_DECAY) {
  const safePrevious = Number.isFinite(previous) ? previous : 0;
  return rms > safePrevious ? rms : safePrevious * decay;
}

/**
 * 包络 → 口型档位
 *
 * @param {number} envelope - 平滑后的包络
 * @returns {0|1|2} 0=闭嘴 1=半开 2=全开
 */
export function mouthFromEnvelope(envelope) {
  if (envelope > MOUTH_OPEN_THRESHOLD) return 2;
  if (envelope > MOUTH_HALF_THRESHOLD) return 1;
  return 0;
}

/**
 * 包络 → 0..1 的显示电平（用于音量条）
 * @param {number} envelope
 * @returns {number}
 */
export function levelFromEnvelope(envelope) {
  return Math.min(1, Math.max(0, envelope) * LEVEL_GAIN);
}

/**
 * 为指定的 media 元素建立（或复用）Web Audio 分析图
 * @param {HTMLMediaElement} element - <audio> 元素
 * @returns {{ ctx: AudioContext, analyser: AnalyserNode, data: Uint8Array } | null}
 */
function ensureGraph(element) {
  const cached = audioGraphCache.get(element);
  if (cached) {
    return cached;
  }

  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) {
    return null;
  }

  try {
    const ctx = new AudioContextClass();
    const source = ctx.createMediaElementSource(element);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.6;

    source.connect(analyser);
    // 必须接回 destination，否则音频静音
    analyser.connect(ctx.destination);

    const graph = {
      ctx,
      source,
      analyser,
      data: new Uint8Array(analyser.fftSize),
    };
    audioGraphCache.set(element, graph);
    return graph;
  } catch (error) {
    // 元素已被别的 AudioContext 绑定过，或环境不支持
    console.warn('[useLipSync] 无法建立音频分析图:', error?.message);
    return null;
  }
}

/**
 * @param {import('react').RefObject<HTMLAudioElement>} audioRef - 音频元素引用
 * @param {Object}  [options]
 * @param {boolean} [options.enabled=true] - 是否运行动画循环（关闭时不消耗 CPU，但绑定照做）
 * @param {boolean} [options.analysis=true] - 是否建立 Web Audio 分析图。
 *   ⚠️ 设为 true 会把 <audio> 的输出路由进 Web Audio，
 *      之后必须靠 AudioContext 才能出声；AudioContext 在无用户手势时
 *      处于 suspended，会导致**完全静音**且前端察觉不到。
 *      **不需要口型/音量数据时务必传 false。**
 * @returns {{ mouth: number, level: number, available: boolean, resume: Function }}
 *   mouth: 0=闭嘴 1=半开 2=全开；level: 0..1 平滑音量；available: 浏览器是否支持
 */
export function useLipSync(audioRef, { enabled = true, analysis = true } = {}) {
  const [mouth, setMouth] = useState(0);
  const [level, setLevel] = useState(0);
  const [available, setAvailable] = useState(true);

  const rafRef = useRef(null);
  const envelopeRef = useRef(0);
  const graphRef = useRef(null);

  useEffect(() => {
    const element = audioRef?.current;
    if (!element) {
      return undefined;
    }

    /*
     * analysis: false → **不碰 Web Audio**。
     *
     * 这会牺牲口型/音量数据，但换来的是音频走浏览器默认通路，
     * 不会因为 AudioContext 起不来而静默无声。
     * 没有口型需求时，这个交换是划算的。
     */
    if (!analysis) {
      setAvailable(false);
      setMouth(0);
      setLevel(0);
      return undefined;
    }

    // 绑定只做一次（ensureGraph 内部用 WeakMap 缓存），
    // 必须尽早绑定：提前建好图可以让首次播放就带上口型，不会有一次「哑口」。
    const graph = ensureGraph(element);
    if (!graph) {
      setAvailable(false);
      return undefined;
    }
    graphRef.current = graph;
    setAvailable(true);

    // enabled 只控制动画循环，不控制绑定
    if (!enabled) {
      setMouth(0);
      setLevel(0);
      envelopeRef.current = 0;
      return undefined;
    }

    const { ctx, analyser, data } = graph;

    const tick = () => {
      // iOS 上 AudioContext 可能处于 suspended，尝试恢复（无用户手势时会静默失败）
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }

      analyser.getByteTimeDomainData(data);

      const rms = computeRms(data);
      const envelope = smoothEnvelope(rms, envelopeRef.current);
      envelopeRef.current = envelope;

      const nextMouth = mouthFromEnvelope(envelope);
      setLevel(levelFromEnvelope(envelope));
      setMouth((prev) => (prev === nextMouth ? prev : nextMouth));

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [audioRef, enabled, analysis]);

  /**
   * 在用户手势回调中调用，恢复被浏览器挂起的 AudioContext（iOS Safari 常见）
   */
  const resume = () => {
    const ctx = graphRef.current?.ctx;
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
  };

  return { mouth, level, available, resume };
}

export default useLipSync;
